import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { getGameData } from '../../data/gameData/registry';
import { GameLifeCounter, mapActiveGameToKey } from '../../components/game/GameLifeCounter';
import { GameSelector, CardGameItem } from '../../components/GameSelector';

interface ToolGameProps {
  activeGame: number | 'All';
  setActiveGame?: (gameId: number | 'All') => void;
  dbGames?: CardGameItem[];
}

const Glossary: React.FC<ToolGameProps> = ({ activeGame, setActiveGame, dbGames }) => {
  const [selectedGameId, setSelectedGameId] = useState<number | 'All'>(activeGame);
  const [isChangingGame, setIsChangingGame] = useState(false);

  useEffect(() => {
    setSelectedGameId(activeGame);
  }, [activeGame]);

  const effectiveGameId = selectedGameId;
  const gameData = getGameData(effectiveGameId);
  const currentTerms = gameData?.glossary || [];
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const categories = useMemo(() => {
    const cats = new Set<string>();
    currentTerms.forEach(t => {
      if (t.category) cats.add(t.category);
    });
    return ['All', ...Array.from(cats)].sort();
  }, [currentTerms]);

  const filteredTerms = currentTerms.filter(t => {
    const matchesSearch = t.term.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         t.definition.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || t.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  if (effectiveGameId === 'All' || isChangingGame) {
    return (
      <div className="py-4 space-y-4">
        {isChangingGame && effectiveGameId !== 'All' && (
          <button
            onClick={() => setIsChangingGame(false)}
            className="text-xs text-slate-400 hover:text-white flex items-center gap-2 mb-2 font-bold transition-colors"
          >
            <i className="fas fa-arrow-left"></i> Voltar ao glossário
          </button>
        )}
        <GameSelector
          games={dbGames}
          selectedGameId={effectiveGameId}
          onSelectGame={(id) => {
            setSelectedGameId(id as number);
            setActiveGame?.(id as number);
            setIsChangingGame(false);
          }}
          title="Glossário Oficial de Regras"
          subtitle="Selecione uma franquia de TCG para consultar termos técnicos e regras oficiais:"
        />
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
        <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500 text-2xl mb-2">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">Aviso: Glossário não encontrado</h3>
        <p className="text-slate-400 max-w-md mx-auto text-sm leading-relaxed font-bold">
          Este jogo ainda não possui um glossário técnico cadastrado em nossa base oficial.
        </p>
        <button
          onClick={() => setIsChangingGame(true)}
          className="mt-4 px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-lg shadow-purple-600/20 inline-flex items-center space-x-2 active:scale-95"
        >
          <i className="fas fa-arrows-rotate text-xs"></i>
          <span>Escolher outro jogo</span>
        </button>
      </div>
    );
  }

  const currentGameName = dbGames?.find(g => String(g.id) === String(effectiveGameId))?.name || 'Jogo Selecionado';

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <span className="text-[10px] font-black uppercase tracking-widest text-purple-400">Glossário Técnico</span>
          <h2 className="text-xl font-black text-white uppercase italic">{currentGameName}</h2>
        </div>
        <button
          onClick={() => setIsChangingGame(true)}
          className="self-start sm:self-auto px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-purple-500 text-slate-300 hover:text-white text-xs font-bold transition-all flex items-center gap-2 shadow-sm"
        >
          <i className="fas fa-arrows-rotate text-purple-400 text-xs"></i>
          <span>Trocar Jogo</span>
        </button>
      </div>

      <div className="flex flex-col space-y-6">
        <div className="relative group">
          <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-purple-400 transition-colors"></i>
          <input 
            type="text"
            placeholder="Pesquisar termo ou mecânica..."
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-white focus:border-purple-500 outline-none transition-all font-bold placeholder:text-slate-700"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2">
            {categories.map((cat: string) => (
              <button
                key={`cat-filter-${cat}`}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeCategory === cat 
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' 
                    : 'bg-slate-800 text-slate-500 hover:text-slate-300 border border-white/5'
                }`}
              >
                {cat === 'All' ? 'Ver Tudo' : cat}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredTerms.length > 0 ? (
            filteredTerms.map((t, i) => (
              <motion.div 
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                key={`term-${t.term}-${i}`} 
                className="group relative bg-[#0d1117] border border-slate-800 rounded-2xl p-6 hover:border-purple-500/30 transition-all shadow-2xl flex flex-col min-h-[160px]"
              >
                {t.category && (
                  <span className="absolute top-4 right-4 text-[8px] font-black uppercase tracking-[0.2em] px-2 py-0.5 bg-purple-600/10 text-purple-400 rounded-md border border-purple-500/20">
                    {t.category}
                  </span>
                )}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-1.5 h-6 bg-purple-600 rounded-full group-hover:scale-y-110 transition-transform"></div>
                  <h3 className="text-white font-black uppercase tracking-tight italic group-hover:text-purple-400 transition-colors">
                    {t.term}
                  </h3>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed font-medium flex-1">
                  {t.definition}
                </p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity rounded-b-2xl"></div>
              </motion.div>
            ))
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full py-20 text-center flex flex-col items-center"
            >
              <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center text-slate-600 mb-4">
                 <i className="fas fa-ghost"></i>
              </div>
              <p className="text-slate-500 italic text-sm font-bold">Nenhum termo encontrado para sua busca.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const FAQ: React.FC<ToolGameProps> = ({ activeGame, setActiveGame, dbGames }) => {
  const [selectedGameId, setSelectedGameId] = useState<number | 'All'>(activeGame);
  const [isChangingGame, setIsChangingGame] = useState(false);

  useEffect(() => {
    setSelectedGameId(activeGame);
  }, [activeGame]);

  const effectiveGameId = selectedGameId;
  const gameData = getGameData(effectiveGameId);
  const currentFaqs = gameData?.faq || [];
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFaqs = currentFaqs.filter(f => 
    f.q.toLowerCase().includes(searchQuery.toLowerCase()) || 
    f.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (effectiveGameId === 'All' || isChangingGame) {
    return (
      <div className="py-4 space-y-4">
        {isChangingGame && effectiveGameId !== 'All' && (
          <button
            onClick={() => setIsChangingGame(false)}
            className="text-xs text-slate-400 hover:text-white flex items-center gap-2 mb-2 font-bold transition-colors"
          >
            <i className="fas fa-arrow-left"></i> Voltar ao FAQ
          </button>
        )}
        <GameSelector
          games={dbGames}
          selectedGameId={effectiveGameId}
          onSelectGame={(id) => {
            setSelectedGameId(id as number);
            setActiveGame?.(id as number);
            setIsChangingGame(false);
          }}
          title="Perguntas Frequentes (FAQ)"
          subtitle="Selecione uma franquia de TCG para consultar dúvidas e respostas oficiais de regras:"
        />
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
        <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center text-blue-500 text-2xl mb-2">
          <i className="fas fa-question-circle"></i>
        </div>
        <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">Aviso: FAQ não encontrado</h3>
        <p className="text-slate-400 max-w-md mx-auto text-sm leading-relaxed font-bold">
          Ainda não temos as perguntas e respostas frequentes cadastradas para este jogo.
        </p>
        <button
          onClick={() => setIsChangingGame(true)}
          className="mt-4 px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all shadow-lg shadow-emerald-600/20 inline-flex items-center space-x-2 active:scale-95"
        >
          <i className="fas fa-arrows-rotate text-xs"></i>
          <span>Escolher outro jogo</span>
        </button>
      </div>
    );
  }

  const currentGameName = dbGames?.find(g => String(g.id) === String(effectiveGameId))?.name || 'Jogo Selecionado';

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">Regras e Dúvidas</span>
          <h2 className="text-xl font-black text-white uppercase italic">{currentGameName}</h2>
        </div>
        <button
          onClick={() => setIsChangingGame(true)}
          className="self-start sm:self-auto px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-500 text-slate-300 hover:text-white text-xs font-bold transition-all flex items-center gap-2 shadow-sm"
        >
          <i className="fas fa-arrows-rotate text-emerald-400 text-xs"></i>
          <span>Trocar Jogo</span>
        </button>
      </div>

      <div className="relative group">
        <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-emerald-400 transition-colors"></i>
        <input 
          type="text"
          placeholder="Pesquisar por dúvida ou regra..."
          className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-white focus:border-emerald-500 outline-none transition-all font-bold placeholder:text-slate-700"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="space-y-4">
        {filteredFaqs.length > 0 ? (
          filteredFaqs.map((f, i) => (
            <div key={i} className="group bg-slate-900/40 border border-slate-800 rounded-[32px] p-6 md:p-8 hover:border-emerald-500/30 transition-all shadow-xl">
              <h3 className="text-white font-black uppercase tracking-tight italic flex items-center mb-4 group-hover:text-emerald-400 transition-colors">
                <span className="w-8 h-8 bg-emerald-600 rounded-xl flex items-center justify-center text-[10px] mr-4 shadow-lg shadow-emerald-500/20 text-white not-italic">Q</span>
                {f.q}
              </h3>
              <div className="flex space-x-4 pl-1">
                 <div className="w-px h-auto bg-slate-800 group-hover:bg-emerald-600/30 transition-colors ml-[15px]"></div>
                 <p className="text-slate-400 text-sm leading-relaxed font-medium py-2">{f.a}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="py-20 text-center flex flex-col items-center">
            <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center text-slate-600 mb-4">
               <i className="fas fa-magnifying-glass"></i>
            </div>
            <p className="text-slate-500 italic text-sm font-bold">Nenhuma dúvida encontrada para sua busca.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export interface ToolsProps {
  activeGame: number | 'All';
  setActiveGame?: (gameId: number | 'All') => void;
  dbGames?: CardGameItem[];
}

export const Tools: React.FC<ToolsProps> = ({ activeGame, setActiveGame, dbGames }) => {
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState<'life' | 'dice' | 'coin' | 'glossary' | 'faq' | null>(null);
  const [showGameSelectorModal, setShowGameSelectorModal] = useState(false);
  const [diceValue, setDiceValue] = useState<number>(20);
  const [isRolling, setIsRolling] = useState(false);
  const [coinResult, setCoinResult] = useState<'CARA' | 'COROA'>('CARA');
  const [isFlipping, setIsFlipping] = useState(false);

  const tools = [
    { id: 'how-to-play', label: 'Como Jogar', icon: 'fa-book-open', color: 'bg-sky-500', desc: 'Guia de regras e fases passo a passo' },
    { id: 'life', label: 'Vida', icon: 'fa-heart', color: 'bg-rose-500', desc: 'Contadores para suas partidas' },
    { id: 'dice', label: 'Dados', icon: 'fa-dice', color: 'bg-purple-600', desc: 'Role D20 ou outros dados' },
    { id: 'coin', label: 'Moeda', icon: 'fa-coins', color: 'bg-yellow-500', desc: 'Sorteie quem começa' },
    { id: 'glossary', label: 'Glossário', icon: 'fa-book', color: 'bg-blue-600', desc: 'Termos técnicos do jogo' },
    { id: 'faq', label: 'FAQ', icon: 'fa-question-circle', color: 'bg-emerald-600', desc: 'Dúvidas e regras comuns' },
  ];

  const rollDice = () => {
    setIsRolling(true);
    setTimeout(() => {
      setDiceValue(Math.floor(Math.random() * 20) + 1);
      setIsRolling(false);
    }, 600);
  };

  const flipCoin = () => {
    setIsFlipping(true);
    setTimeout(() => {
      setCoinResult(Math.random() > 0.5 ? 'CARA' : 'COROA');
      setIsFlipping(false);
    }, 600);
  };

  const activeGameObj = dbGames?.find(g => String(g.id) === String(activeGame));

  // Reset active tool when switching to All games
  useEffect(() => {
    if (activeGame === 'All' || !activeGame) {
      setActiveTool(null);
    }
  }, [activeGame]);

  // O usuário precisa escolher o jogo antes de ver as ferramentas
  if (activeGame === 'All' || !activeGame) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          onSelectGame={(id) => {
            if (setActiveGame) {
              setActiveGame(id as number);
            }
          }}
          title="Escolha um Jogo"
          subtitle="Para acessar as ferramentas personalizadas, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  return (
    <div className={activeTool === 'life' ? 'h-full' : 'space-y-8'}>
      {activeTool !== 'life' && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center">
              {activeTool ? (
                <button 
                  onClick={() => setActiveTool(null)} 
                  className="mr-3 p-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors"
                  title="Voltar para ferramentas"
                >
                  <i className="fas fa-arrow-left text-sm"></i>
                </button>
              ) : (
                <i className="fas fa-toolbox mr-3 text-purple-500"></i>
              )}
              {activeTool ? tools.find(t => t.id === activeTool)?.label : 'Ferramentas'}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {activeTool ? tools.find(t => t.id === activeTool)?.desc : 'Utilitários para suas partidas e torneios.'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (setActiveGame) {
                  setActiveGame('All');
                } else {
                  setShowGameSelectorModal(true);
                }
              }}
              className="px-3.5 py-2 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-500/50 text-slate-300 hover:text-white text-xs font-bold flex items-center gap-2 transition-all shadow-sm active:scale-95"
              title="Trocar jogo selecionado"
            >
              <i className="fas fa-arrows-rotate text-purple-400 text-xs"></i>
              <span className="truncate max-w-[150px] sm:max-w-none">
                {activeGameObj?.name || 'Trocar Jogo'}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Global Game Selector Modal for Tools */}
      <AnimatePresence>
        {showGameSelectorModal && (
          <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto space-y-4 shadow-2xl relative my-auto"
            >
              <button
                onClick={() => setShowGameSelectorModal(false)}
                className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors border border-white/10"
                title="Fechar seletor"
              >
                <i className="fas fa-times"></i>
              </button>

              <GameSelector
                games={dbGames}
                selectedGameId={activeGame}
                onSelectGame={(id) => {
                  setActiveGame?.(id as number);
                  setShowGameSelectorModal(false);
                }}
                title="Foco de Card Game"
                subtitle="Selecione o TCG ativo para contadores, termos de regras e referências:"
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {!activeTool ? (
          <motion.div 
            key="grid"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {tools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => {
                  if (tool.id === 'how-to-play') {
                    const slug = activeGameObj?.slug || '';
                    navigate(slug ? `/como-jogar/${slug}` : '/como-jogar');
                    return;
                  }
                  setActiveTool(tool.id as any);
                }}
                className="group relative bg-slate-900/50 border border-slate-800 rounded-3xl p-8 text-left hover:border-purple-500/50 transition-all shadow-xl hover:shadow-purple-500/5 overflow-hidden"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 ${tool.color}/10 rounded-bl-full transform translate-x-10 -translate-y-10 group-hover:scale-110 transition-transform duration-500`}></div>
                
                <div className={`w-14 h-14 ${tool.color} rounded-2xl flex items-center justify-center text-2xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`fas ${tool.icon} text-white`}></i>
                </div>
                
                <h3 className="text-xl font-black text-white mb-2">{tool.label}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{tool.desc}</p>
                
                <div className="mt-6 flex items-center text-[10px] font-black uppercase tracking-widest text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  Abrir ferramenta <i className="fas fa-arrow-right ml-2 scale-75"></i>
                </div>
              </button>
            ))}
          </motion.div>
        ) : activeTool === 'life' ? (
          <motion.div 
            key="life-counter-tool"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full h-full"
          >
            <GameLifeCounter 
              initialGame={mapActiveGameToKey(activeGame)} 
              onBack={() => setActiveTool(null)}
            />
          </motion.div>
        ) : (
          <motion.div 
            key="content"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-slate-900/30 border border-slate-800/50 rounded-[40px] p-6 md:p-12 min-h-[500px]"
          >
            {activeTool === 'dice' && (
              <div className="flex flex-col items-center justify-center space-y-10 py-12">
                <motion.div 
                  animate={isRolling ? { rotate: [0, 90, 180, 270, 360], scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: isRolling ? Infinity : 0 }}
                  className="w-56 h-56 bg-slate-800 rounded-3xl border-4 border-white/10 flex items-center justify-center text-8xl font-black text-white shadow-2xl relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent"></div>
                  {diceValue}
                </motion.div>
                <div className="text-center">
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px] mb-6">Role um Dado de 20 Lados</p>
                  <button 
                    onClick={rollDice}
                    disabled={isRolling}
                    className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-12 py-5 rounded-2xl shadow-xl transition-all active:scale-95 text-lg"
                  >
                    {isRolling ? 'ROLANDO...' : 'ROLAR DADO'}
                  </button>
                </div>
              </div>
            )}

            {activeTool === 'coin' && (
              <div className="flex flex-col items-center justify-center space-y-10 py-12">
                 <motion.div 
                  animate={isFlipping ? { rotateY: [0, 180, 360, 540, 720], y: [0, -100, 0] } : {}}
                  transition={{ duration: 0.6 }}
                  className={`w-56 h-56 rounded-full border-8 flex items-center justify-center text-4xl font-black shadow-2xl ${coinResult === 'CARA' ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-500' : 'bg-slate-500/20 border-slate-500/40 text-slate-300'}`}
                 >
                  {coinResult}
                </motion.div>
                <div className="text-center">
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px] mb-6">Cara ou Coroa</p>
                  <button 
                    onClick={flipCoin}
                    disabled={isFlipping}
                    className="bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-white font-black px-12 py-5 rounded-2xl shadow-xl transition-all active:scale-95 text-lg"
                  >
                    {isFlipping ? 'GIRANDO...' : 'JOGAR MOEDA'}
                  </button>
                </div>
              </div>
            )}

            {activeTool === 'glossary' && (
              <Glossary 
                activeGame={activeGame} 
                setActiveGame={setActiveGame} 
                dbGames={dbGames} 
              />
            )}
            {activeTool === 'faq' && (
              <FAQ 
                activeGame={activeGame} 
                setActiveGame={setActiveGame} 
                dbGames={dbGames} 
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
